import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { formatDate } from '../../utils/helpers';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Handshake, Check, X, Snowflake } from 'lucide-react';

const STATUS_STYLES = {
  active: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  pending: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  suspended: 'bg-red-500/15 text-red-600 border-red-500/30',
  closed: 'bg-muted text-muted-foreground border-border',
  pending_admin: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  approved: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  rejected: 'bg-red-500/15 text-red-600 border-red-500/30',
};

export const AdminGroupsPage = () => {
  const [groups, setGroups] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectMode, setRejectMode] = useState('group'); // 'group' or 'withdrawal'

  const load = async () => {
    setLoading(true);
    try {
      const [g, w] = await Promise.all([
        adminAPI.listGroups(statusFilter),
        adminAPI.listGroupWithdrawals('pending_admin'),
      ]);
      setGroups(g.data);
      setWithdrawals(w.data);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const approve = async (g) => {
    try { await adminAPI.approveGroup(g.id); toast.success('Approved'); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const suspend = async (g) => {
    try { await adminAPI.suspendGroup(g.id); toast.success('Toggled'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  const approveWithdrawal = async (w) => {
    if (!window.confirm(`Approve withdrawal of ${w.amount} ${w.currency} from ${w.group_name}?`)) return;
    try { await adminAPI.approveGroupWithdrawal(w.id); toast.success('Approved'); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) return toast.error('Reason required');
    try {
      if (rejectMode === 'group') await adminAPI.rejectGroup(rejectFor.id, rejectReason.trim());
      else await adminAPI.rejectGroupWithdrawal(rejectFor.id, rejectReason.trim());
      toast.success('Rejected');
      setRejectFor(null); setRejectReason('');
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-groups-page">
        <div>
          <h1 className="text-4xl font-semibold flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Handshake className="h-8 w-8 text-purple-600" /> Community Savings Groups
          </h1>
          <p className="mt-2 text-muted-foreground">Approve, suspend and moderate stokvel groups</p>
        </div>

        <Tabs defaultValue="groups">
          <TabsList>
            <TabsTrigger value="groups" data-testid="tab-groups">Groups ({groups.length})</TabsTrigger>
            <TabsTrigger value="withdrawals" data-testid="tab-group-withdrawals">
              Pending Withdrawals ({withdrawals.length})
              {withdrawals.length > 0 && <span className="ml-2 h-2 w-2 rounded-full bg-amber-500 animate-pulse" />}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="groups">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  {['pending', 'active', 'suspended', 'closed'].map(s => (
                    <Button key={s} size="sm" variant={statusFilter === s ? 'default' : 'outline'} onClick={() => setStatusFilter(s)} data-testid={`filter-${s}`}>
                      {s}
                    </Button>
                  ))}
                </div>
              </CardHeader>
              <CardContent>
                {loading ? <p className="text-center py-8 text-muted-foreground">Loading...</p> : groups.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No {statusFilter} groups.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Currency</TableHead>
                        <TableHead>Members</TableHead>
                        <TableHead>Treasury</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groups.map(g => (
                        <TableRow key={g.id} data-testid={`group-row-${g.id}`}>
                          <TableCell className="font-medium">{g.name}</TableCell>
                          <TableCell className="text-sm capitalize">{g.type?.replace('_', ' ')}</TableCell>
                          <TableCell>{g.currency}</TableCell>
                          <TableCell>{g.member_count}</TableCell>
                          <TableCell className="font-mono text-sm">{((g.treasury?.operating || 0) + (g.treasury?.savings || 0) + (g.treasury?.emergency || 0)).toFixed(2)}</TableCell>
                          <TableCell className="text-xs">{formatDate(g.created_at)}</TableCell>
                          <TableCell><Badge variant="outline" className={STATUS_STYLES[g.status]}>{g.status}</Badge></TableCell>
                          <TableCell className="text-right space-x-1">
                            {g.status === 'pending' && (
                              <>
                                <Button size="sm" onClick={() => approve(g)} data-testid={`approve-group-${g.id}`}><Check className="h-3 w-3 mr-1" /> Approve</Button>
                                <Button size="sm" variant="destructive" onClick={() => { setRejectFor(g); setRejectMode('group'); }} data-testid={`reject-group-${g.id}`}><X className="h-3 w-3 mr-1" /> Reject</Button>
                              </>
                            )}
                            {(g.status === 'active' || g.status === 'suspended') && (
                              <Button size="sm" variant="outline" onClick={() => suspend(g)} data-testid={`suspend-group-${g.id}`}>
                                <Snowflake className="h-3 w-3 mr-1" /> {g.status === 'suspended' ? 'Unsuspend' : 'Suspend'}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="withdrawals">
            <Card>
              <CardContent className="pt-6">
                {withdrawals.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No pending group withdrawals.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Group</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Pool</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Requested</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {withdrawals.map(w => (
                        <TableRow key={w.id} data-testid={`gw-row-${w.id}`}>
                          <TableCell className="font-medium">{w.group_name}</TableCell>
                          <TableCell className="font-mono">{w.amount} {w.currency}</TableCell>
                          <TableCell className="capitalize">{w.pool}</TableCell>
                          <TableCell className="text-sm max-w-xs truncate">{w.reason}</TableCell>
                          <TableCell className="text-xs">{formatDate(w.created_at)}</TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button size="sm" onClick={() => approveWithdrawal(w)} data-testid={`approve-gw-${w.id}`}><Check className="h-3 w-3 mr-1" /> Approve</Button>
                            <Button size="sm" variant="destructive" onClick={() => { setRejectFor(w); setRejectMode('withdrawal'); }} data-testid={`reject-gw-${w.id}`}><X className="h-3 w-3 mr-1" /> Reject</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!rejectFor} onOpenChange={(v) => !v && setRejectFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject {rejectMode === 'group' ? 'Group' : 'Withdrawal'}</DialogTitle>
            <DialogDescription>The requester will be notified. Action is logged permanently.</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} placeholder="Reason for rejection..." value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doReject} data-testid="confirm-reject-btn">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};
